/**
 * Content Manager - Authentication Store
 *
 * Zustand store for managing authentication state and user session.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types';

// =============================================================================
// TYPES
// =============================================================================

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  accessToken: string | null;
  isLoading: boolean;
  error: string | null;
  hasHydrated: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  login: (user: User, token: string) => void;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  setHasHydrated: (state: boolean) => void;
}

// =============================================================================
// STORE
// =============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      accessToken: null,
      isLoading: false,
      error: null,
      hasHydrated: false,

      // Set user
      setUser: (user) => {
        set({
          user,
          isAuthenticated: user !== null,
        });
      },

      // Set access token
      setToken: (token) => {
        set({ accessToken: token });
      },

      // Set loading state
      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      // Set error
      setError: (error) => {
        set({ error });
      },

      // Login action
      login: (user, token) => {
        set({
          user,
          accessToken: token,
          isAuthenticated: true,
          error: null,
        });
      },

      // Logout action
      logout: () => {
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          error: null,
        });
      },

      // Update user partial data
      updateUser: (updates) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: { ...currentUser, ...updates },
          });
        }
      },

      // Set hydration state
      setHasHydrated: (state) => {
        set({ hasHydrated: state });
      },
    }),
    {
      name: 'content-manager-auth',
      partialize: (state) => ({
        // Only persist these fields
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Called when hydration is complete
        state?.setHasHydrated(true);
      },
    }
  )
);

// =============================================================================
// SELECTORS
// =============================================================================

export const selectUser = (state: AuthState) => state.user;
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated;
export const selectIsAdmin = (state: AuthState) => state.user?.role === 'Admin';
export const selectIsEditor = (state: AuthState) =>
  state.user?.role === 'Admin' || state.user?.role === 'Editor';
export const selectCanEdit = (state: AuthState) =>
  state.user?.role === 'Admin' || state.user?.role === 'Editor';
export const selectUserRole = (state: AuthState) => state.user?.role;
export const selectHasHydrated = (state: AuthState) => state.hasHydrated;

export default useAuthStore;
